import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Merkmal, MerkmalLock } from '@models/merkmalbaum';
import { select, Store } from '@ngrx/store';
import { I18nService } from 'app/core/services/i18n/i18n.service';
import * as MerkmalActions from 'app/modules/shared-area/ducks/merkmal/store';
import {
  getMerkmale,
} from 'app/modules/shared-area/ducks/merkmal/store';
import { getLieferantenMerkmale } from 'app/modules/shared-area/ducks/user-profile/store/user-profile.selectors';
import { IAppState } from 'app/modules/shared-area/root-state';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { first, map, tap, filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-select-merkmal-dialog',
  templateUrl: './select-merkmal-dialog.component.html',
  styleUrls: ['./select-merkmal-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectMerkmalDialogComponent implements OnInit, OnDestroy {
  @Input()
  public merkmal: string;

  @Input()
  public merkmale: { [key: string]: boolean } = {};

  @Input()
  public showAllMerkmale = false;

  @Input()
  merkmalLocks: MerkmalLock[] = [];

  @Input()
  public enableLocks = true;

  @Input()
  public limitLevel: number | null = null;

  @Input()
  public showFeldNodes: boolean;

  @Input()
  public filterByLieferant: boolean;

  @Input()
  public selectableNodes?: Set<string>;

  @Input()
  public allowedDossierType: number | null = null;

  @Input()
  public excludeRoot = false;

  @Input()
  public isMultiselectable = false;

  @Input()
  public showApplyButton = false;

  @Output()
  public selectMerkmal: EventEmitter<Merkmal> = new EventEmitter();

  @Output()
  public selectMerkmale: EventEmitter<{ [key: string]: boolean }> = new EventEmitter();

  public merkmalTree: Merkmal[] | null = null;
  public matchedNode: string;
  public matches: string[];
  public expandedKeys = [];
  public readonly searchForm: UntypedFormGroup = this.fb.group({
    searchTerm: '',
  });
  private index: number;
  private alleMerkmale: string[] = [];
  private lastSearchQuery: string = '';
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<IAppState>,
    @Inject(I18nService) private i18n: I18nService,
    private fb: UntypedFormBuilder,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    this.store.pipe(
      select(getMerkmale),
      filter(merkmale => merkmale !== null && merkmale !== undefined),
      first(),
      map(merkmale => {
        let filteredMerkmale = merkmale;
        
        // Apply lieferant filter if requested
        if (this.filterByLieferant) {
          let lieferantenMerkmale: Set<number>;
          this.store.pipe(
            first(),
            select(getLieferantenMerkmale)
          ).subscribe(merkmale => lieferantenMerkmale = merkmale);
          
          const rootTree = { items: merkmale, key: 'ROOT', name: 'root', id: -1, type: 'Hierarchie' as const };
          this.filterMerkmalTreeByLieferant(rootTree, lieferantenMerkmale);
          filteredMerkmale = rootTree.items;
        }
        
        this.alleMerkmale = [];
        const tree = this.createHierarchyTree(filteredMerkmale, '', this.merkmalLocks, this.showFeldNodes);
        return tree;
      }),
      takeUntil(this.destroy$)
    ).subscribe(merkmale => {
      this.merkmalTree = merkmale;
      this.initialSearchByMerkmal();
      this.cdr.markForCheck();
    });
    
    this.store.dispatch(MerkmalActions.loadMerkmale());
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateScrollOffset(): void {
    const stickyHeader = this.elementRef.nativeElement.querySelector('.sticky-header') as HTMLElement;
    if (stickyHeader) {
      const headerHeight = stickyHeader.offsetHeight;
      // Set CSS custom property for scroll-margin-top
      this.elementRef.nativeElement.style.setProperty('--scroll-offset', `${headerHeight + 3}px`);
    }
  }

  public onSelectionChange(entry: any): void {
    const merkmal: Merkmal = entry.dataItem;
    if (!merkmal.isLocked && !merkmal.isDisabled) {
      this.selectMerkmal.emit(merkmal);
    }
  }

  public onSubmit() {
    this.search(this.searchForm.value.searchTerm);
  }

  public get hasMatches() {
    return this.matches && this.matches.length > 0;
  }

  public prev() {
    if (this.matches && this.matches.length > 0) {
      this.index--;
      if (this.index < 0) {
        this.index = this.matches.length - 1;
      }
      this.matchedNode = null;
      this.expandedKeys = [];
      this.expandByKey(this.matches[this.index]);
    }
  }

  public next() {
    if (this.matches && this.matches.length > 0) {
      this.index++;
      if (this.index >= this.matches.length) {
        this.index = 0;
      }
      this.matchedNode = null;
      this.expandedKeys = [];
      this.expandByKey(this.matches[this.index]);
    }
  }

  public isLockedBy(merkmal: Merkmal, locks: MerkmalLock[]): string {
    const isLocked = locks.find(lock => lock.merkmalId === merkmal.id && merkmal.type === 'Merkmal');
    if (isLocked) {
      return this.i18n.getLocalizedString('components.dossier.lockedBy').replace('{{value}}', isLocked.lockedBy);
    }
    return null;
  }

  public toggleIsChecked(key: string, isChecked: boolean) {
    const isMerkmal = key.indexOf('-M') > -1;
    if (isMerkmal) {
      this.checkMerkmal(key, isChecked);
    } else {
      this.alleMerkmale
        .filter(merkmal => merkmal.startsWith(key + '-'))
        .forEach(merkmal => {
          this.checkMerkmal(merkmal, isChecked);
        });
    }
  }

  /**
   * Gets checkbox state information, calculating both checked and indeterminate states efficiently
   * @param key hierarchy key
   * @param name display name of the item
   * @returns object with checked, indeterminate state and localized aria-label/title
   */
  public getCheckboxState(key: string, name: string) {
    const isMerkmal = key.indexOf('-M') > -1;
    let checked: boolean;
    let indeterminate: boolean;

    if (isMerkmal) {
      // For Merkmal nodes: directly check the value, never indeterminate
      // If showAllMerkmale is true, merkmal is checked unless explicitly false
      if (this.showAllMerkmale) {
        checked = this.merkmale[key] !== false;
      } else {
        checked = this.merkmale[key] === true;
      }
      indeterminate = false;
    } else {
      // For Hierarchie nodes: check children once and calculate both states
      const children = this.alleMerkmale.filter(merkmal => merkmal.startsWith(key + '-'));
      const checkedCount = children.filter(merkmal => {
        if (this.showAllMerkmale) {
          return this.merkmale[merkmal] !== false;
        } else {
          return this.merkmale[merkmal] === true;
        }
      }).length;
      
      checked = checkedCount === children.length && children.length > 0;
      indeterminate = checkedCount > 0 && checkedCount < children.length;
    }

    const indeterminateText = this.i18n.getLocalizedString('dialog.indeterminateCheckbox');
    
    return {
      checked,
      indeterminate,
      ariaLabel: indeterminate ? `${name} (${indeterminateText})` : name,
      title: indeterminate ? indeterminateText : ''
    };
  }

  public applyMerkmale() {
    this.selectMerkmale.emit(this.merkmale);
  }

  private filterMerkmalTreeByLieferant(tree: Merkmal, lieferanten: Set<number>): void {
    const items = tree.items;
    for (let i = items.length - 1; i > -1; i--) {
      const item: Merkmal = items[i];
      switch (item.type) {
        case 'Hierarchie':
          if (item.items && item.items.length > 0) {
            this.filterMerkmalTreeByLieferant(item, lieferanten);
            if (item.items.length === 0) {
              items.splice(i, 1);
            }
          } else {
            items.splice(i, 1);
          }
          break;
        case 'Merkmal':
          if (!lieferanten.has(item.id)) {
            items.splice(i, 1);
          }
          break;
      }
    }
  }

  private initialSearchByMerkmal() {
    if (!_.isNil(this.merkmal)) {
      this.searchForm.controls['searchTerm'].setValue(this.merkmal);
      this.search(this.merkmal);
    }
  }

  private createHierarchyTree(
    merkmale: Merkmal[],
    parentId: string,
    locks: MerkmalLock[],
    showFeldNodes = false,
  ): Merkmal[] {
    return merkmale.map(merkmal => {
      const isMerkmal = merkmal.type === 'Merkmal';
      const id = !isMerkmal ? `H${merkmal.id}` : `M${merkmal.id}`;
      const anchor = [parentId, id].filter(x => !!x).join('-');

      if (isMerkmal) {
        this.alleMerkmale.push(anchor);
      }

      const clonedMerkmal: Merkmal = {
        ...merkmal,
        hierarchy: anchor,
      };

      if (this.enableLocks) {
        const isLockedBy = this.isLockedBy(merkmal, locks);
        clonedMerkmal.isLocked = !_.isNil(isLockedBy);
        clonedMerkmal.lockedBy = isLockedBy;
      }

      if (this.selectableNodes) {
        clonedMerkmal.isDisabled = !this.selectableNodes.has(merkmal.key);
      }

      if (this.excludeRoot && merkmal.root === true) {
        clonedMerkmal.isDisabled = true;
      }

      if (
        this.allowedDossierType &&
        merkmal.allowedTypes &&
        merkmal.allowedTypes.indexOf(this.allowedDossierType) === -1
      ) {
        clonedMerkmal.isDisabled = true;
      }

      const hasChildHierarchie = _.some(clonedMerkmal.items, ['type', 'Hierarchie']);
      const hasChildMerkmal = _.some(clonedMerkmal.items, ['type', 'Merkmal']);

      if (showFeldNodes || hasChildHierarchie || hasChildMerkmal) {
        if (clonedMerkmal.items && clonedMerkmal.items.length > 0) {
          clonedMerkmal.items = this.createHierarchyTree(clonedMerkmal.items, anchor, locks, showFeldNodes);
        }
      } else {
        clonedMerkmal.items = null;
      }
      return clonedMerkmal;
    });
  }

  private search(query: string) {
    // If same query, jump to next result instead of searching again
    if (query === this.lastSearchQuery && this.matches && this.matches.length > 0) {
      this.next();
      return;
    }

    this.lastSearchQuery = query;
    this.matches = [];
    this.index = 0;
    this.matchedNode = null;
    this.expandedKeys = [];

    this.filterBySearchType(query, this.merkmalTree);
    this.expandAndScrollToSearchResult();
  }

  private expandAndScrollToSearchResult() {
    if (this.matches && this.matches.length > 0) {
      this.expandByKey(this.matches[0]);
      this.scrollTo();
    }
  }

  private filterBySearchType(query: string, merkmale: Merkmal[]) {
    const rootMerkmal: Merkmal = {
      items: merkmale,
      key: 'ROOT',
      name: 'root',
      id: -1,
      type: 'Hierarchie',
    };
    // First search by key (exact match)
    this.filterByKey(rootMerkmal, query);
    // Then search by name (partial match)
    this.filterByName(rootMerkmal, query);
    // Remove duplicates from matches array
    this.matches = [...new Set(this.matches)];
  }

  private filterByKey(merkmal: Merkmal, searchTerm: string): void {
    const key = merkmal.key.toUpperCase();
    const isMatch: boolean = key === searchTerm.toUpperCase();
    merkmal.isMatch = isMatch;
    if (isMatch) {
      this.matches.push(merkmal.hierarchy);
    }
    if (merkmal.items && merkmal.items.length > 0) {
      merkmal.items.forEach(item => {
        this.filterByKey(item, searchTerm);
      });
    }
  }

  private filterByName(merkmal: Merkmal, searchTerm: string): void {
    const name = merkmal.name.toUpperCase();
    const isMatch: boolean = name.indexOf(searchTerm.toUpperCase()) > -1;
    merkmal.isMatch = isMatch;
    if (isMatch) {
      this.matches.push(merkmal.hierarchy);
    }
    if (merkmal.items && merkmal.items.length > 0) {
      merkmal.items.forEach(item => {
        this.filterByName(item, searchTerm);
      });
    }
  }

  private expandByKey(key: string, scrollToKey = true) {
    if (scrollToKey && _.isNil(this.matchedNode)) {
      this.matchedNode = key;
    }
    this.expandedKeys.push(key);
    if (key.lastIndexOf('-') !== -1) {
      this.expandByKey(key.substring(0, key.lastIndexOf('-')), scrollToKey);
    } else if (scrollToKey) {
      this.scrollTo();
    }
  }

  private scrollTo(): void {
    setTimeout(() => {
      this.updateScrollOffset();
      const dialogElement = document.getElementsByTagName('app-select-merkmal-dialog')[0];
      if (!dialogElement) {
        return;
      }

      const matchedElement = dialogElement.getElementsByClassName(`${this.matchedNode}`)[0] as HTMLElement;
      if (matchedElement) {
        // scrollIntoView will find k-window-content parent automatically
        // scroll-margin-top CSS handles sticky header offset
        matchedElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 400);
  }

  private checkMerkmal(key: string, isChecked: boolean) {
    this.merkmale = { ...this.merkmale };
    this.merkmale[key] = isChecked;
  }
}
